/**
 * useRequirePersona — route-level persona guard (Forwarder TMS Wave 1.3).
 *
 * The hook half of the frontend persona-gating layer. A page or route guard
 * calls this to redirect away when the tenant lacks the required persona, so
 * forwarder-only routes are unreachable for non-forwarder tenants. The render
 * gate for *fragments* of a page is `<IfPersona>`; this is for whole routes.
 *
 * While the persona is still loading, `isLoading` is true and no redirect
 * happens yet — callers should render a loading state and avoid mounting the
 * protected content until `allowed` is known.
 *
 * Pairs with `PersonaRoute`, the declarative wrapper used in App.tsx routes.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePersona } from './usePersona';

export interface RequirePersonaOptions {
  /** Require the tenant to operate as a freight forwarder (sells_freight_services). */
  forwarder?: boolean;
  /** Where to send the user when the persona requirement isn't met. Defaults to home. */
  redirectTo?: string;
}

export function useRequirePersona({
  forwarder = false,
  redirectTo = '/',
}: RequirePersonaOptions = {}) {
  const navigate = useNavigate();
  // `isPending` (no data yet), NOT `isLoading` (= isPending && isFetching):
  // on a hard navigation the persona query can be pending-but-idle for a tick
  // (auth-gated `enabled`, StrictMode effect interleaving) and isLoading=false
  // would bounce a fully-entitled user off the route before the fetch starts.
  const { data: persona, isPending } = usePersona();

  const allowed = !forwarder || (persona?.sellsFreightServices ?? false);

  useEffect(() => {
    // Redirect once the query has SETTLED (data or error) and the requirement
    // isn't met. An errored persona query still fails closed (isPending=false,
    // no data) — a forwarder-only route redirects home rather than rendering a
    // blank page. The backend 404 is the real boundary.
    if (!isPending && !allowed) {
      navigate(redirectTo, { replace: true });
    }
  }, [isPending, allowed, navigate, redirectTo]);

  return { isLoading: isPending, allowed };
}
